import { FC, useMemo } from 'react';
import LaunchIcon from '@mui/icons-material/Launch';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, IconButton, Skeleton, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { ScalarLineChart } from 'components/Charts';
import { MutualFundMark, MutualFundMarksResponse } from 'services/hooks/companies';
import {
  AvgPrice,
  CardHeaderRow,
  ChangeLabel,
  FundFilterRow,
  FundPill,
  HeaderLeft,
  MarksTable,
  MarksTableCell,
  MarksTableHead,
  MarksTableRow,
  ViewToggle,
} from './MutualFundMarksCard.styles';
import {
  buildChartSeries,
  computeHeaderStats,
  EndDateMode,
  EndDateModeValue,
  formatChartDate,
  formatCurrency,
  formatIsoDate,
  formatXAxisLabel,
  getFundLabel,
  getPeriodStartDate,
  getPricePerShare,
  Period,
  PERIODS,
  PeriodValue,
  toNum,
  ViewMode,
  ViewModeValue,
} from './MutualFundMarksCard.utils';

interface Props {
  data: MutualFundMarksResponse | undefined;
  isLoading: boolean;
  measurementDate?: string;
  endDateMode: EndDateModeValue;
  view: ViewModeValue;
  onViewChange: (v: ViewModeValue) => void;
  period: PeriodValue;
  onPeriodChange: (v: PeriodValue) => void;
  selectedFund: string | null;
  onSelectedFundChange: (v: string | null) => void;
}

const EdgarMarksBody: FC<Props> = ({
  data,
  isLoading,
  measurementDate,
  endDateMode,
  view,
  onViewChange,
  period,
  onPeriodChange,
  selectedFund,
  onSelectedFundChange,
}) => {
  const uniqueFundLabels = data?.fund_families ?? [];

  const latestDataDate = useMemo(() => {
    if (!data?.marks.length) {
      return undefined;
    }
    return [...data.marks].sort((a, b) => b.reporting_date.localeCompare(a.reporting_date))[0].reporting_date;
  }, [data]);

  const effectiveEndDate = useMemo(() => endDateMode === EndDateMode.Measurement && measurementDate
    ? measurementDate
    : latestDataDate, [endDateMode, measurementDate, latestDataDate]);

  const filteredMarks = useMemo(() => {
    if (!data) {
      return [];
    }
    let { marks } = data;

    if (effectiveEndDate) {
      marks = marks.filter(m => m.reporting_date <= effectiveEndDate);
    }

    if (period !== Period.All && effectiveEndDate) {
      const startDate = getPeriodStartDate(effectiveEndDate, period);
      if (startDate) {
        marks = marks.filter(m => m.reporting_date >= startDate);
      }
    }

    if (selectedFund) {
      marks = marks.filter(m => getFundLabel(m) === selectedFund);
    }

    return marks;
  }, [data, period, effectiveEndDate, selectedFund]);

  // Header value is always the weighted average of the last 3 months relative to
  // the effective end date, regardless of the period the user has selected for the chart.
  const headerMarks = useMemo(() => {
    if (!data || !effectiveEndDate) {
      return [];
    }
    let marks = data.marks.filter((m: MutualFundMark) => m.reporting_date <= effectiveEndDate);
    const startDate = getPeriodStartDate(effectiveEndDate, Period.M3);
    if (startDate) {
      marks = marks.filter((m: MutualFundMark) => m.reporting_date > startDate);
    }
    return marks;
  }, [data, effectiveEndDate]);

  const { series, singleFund } = useMemo(() => buildChartSeries(filteredMarks), [filteredMarks]);
  const { headerValue } = useMemo(() => computeHeaderStats(headerMarks), [headerMarks]);
  const { change } = useMemo(() => computeHeaderStats(filteredMarks), [filteredMarks]);

  const hasData = (data?.marks.length ?? 0) > 0;

  return (
    <>
      <CardHeaderRow>
        <HeaderLeft>
          {hasData && (
            <>
              <Tooltip
                title={
                  <Box sx={{ fontSize: '0.75rem', lineHeight: 1.6 }}>
                    {[...headerMarks]
                      .sort((a, b) => a.reporting_date.localeCompare(b.reporting_date))
                      .map((m: MutualFundMark) => {
                        const pps = getPricePerShare(m);
                        return (
                          <Box key={`${m.fund_name}-${m.reporting_date}`} sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                            <span>{getFundLabel(m)} · {formatIsoDate(m.reporting_date)}</span>
                            <span>{formatCurrency(pps)}</span>
                          </Box>
                        );
                      })}
                  </Box>
                }
                placement="bottom-start"
              >
                <AvgPrice>{formatCurrency(headerValue)}</AvgPrice>
              </Tooltip>
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                avg price / share
              </Typography>
              {change && (
                <ChangeLabel positive={change.abs >= 0}>
                  {change.abs >= 0 ? '+' : ''}{change.abs.toFixed(2)} ({change.abs >= 0 ? '+' : ''}{change.pct.toFixed(2)}%)
                </ChangeLabel>
              )}
              {latestDataDate && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontStyle: 'italic' }}>
                  Data through {formatIsoDate(effectiveEndDate && effectiveEndDate < latestDataDate ? effectiveEndDate : latestDataDate)}
                </Typography>
              )}
            </>
          )}
        </HeaderLeft>
        <ViewToggle>
          {hasData && (
            <ToggleButtonGroup
              value={period}
              exclusive
              size="small"
              onChange={(_e, v: PeriodValue | null) => {
                if (v) {
                  onPeriodChange(v);
                }
              }}
            >
              {PERIODS.map(p => (
                <ToggleButton key={p} value={p}>{p}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
          <ToggleButtonGroup
            value={view}
            exclusive
            size="small"
            onChange={(_e, v: ViewModeValue | null) => {
              if (v) {
                onViewChange(v);
              }
            }}
          >
            <ToggleButton value={ViewMode.Chart}>Chart</ToggleButton>
            <ToggleButton value={ViewMode.Table}>Table</ToggleButton>
          </ToggleButtonGroup>
          <Button
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon sx={{ fontSize: '0.7rem !important' }} />}
            href="https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&type=NPORT-P&dateb=&owner=include&count=40"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'none', borderColor: 'warning.main', color: 'warning.dark', height: 26, py: 0 }}
          >
            SEC Filing
          </Button>
        </ViewToggle>
      </CardHeaderRow>

      {isLoading && (
        <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 1 }} />
      )}

      {!isLoading && !hasData && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3 }}>
          No mutual fund marks available for this company.
        </Typography>
      )}

      {!isLoading && hasData && (
        <>
          {uniqueFundLabels.length > 1 && (
            <FundFilterRow>
              {uniqueFundLabels.map((label: string) => (
                <FundPill
                  key={label}
                  label={label}
                  size="small"
                  variant="outlined"
                  active={selectedFund === label}
                  onClick={() => onSelectedFundChange(selectedFund === label ? null : label)}
                />
              ))}
              <FundPill
                label="All Funds"
                size="small"
                variant="outlined"
                active={selectedFund === null}
                onClick={() => onSelectedFundChange(null)}
              />
            </FundFilterRow>
          )}

          {view === ViewMode.Chart && (
            <ScalarLineChart
              series={series}
              notMerge
              smooth
              showPoints
              style={{ height: 280 }}
              config={{
                legend: { show: true, position: 'top' },
                xAxis: { formatter: v => formatXAxisLabel(v, singleFund) },
                yAxis: { formatter: v => `$${v}` },
                options: {
                  tooltip: {
                    trigger: 'axis',
                    appendToBody: true,
                    formatter: (params: any) => {
                      if (!Array.isArray(params) || !params.length) return '';
                      const dateLabel = formatChartDate(String(params[0].axisValue));
                      return (
                        `<div style="font-weight:700;margin-bottom:4px;font-size:0.82rem">${dateLabel}</div>${params
                          .filter((p: any) => p.value != null)
                          .map((p: any) => `<div>${p.marker}${p.seriesName}: <b>$${Number(p.value).toFixed(2)}</b></div>`)
                          .join('')}`
                      );
                    },
                  },
                },
              }}
            />
          )}

          {view === ViewMode.Table && (
            <MarksTable>
              <thead>
                <MarksTableRow>
                  <MarksTableHead>Fund</MarksTableHead>
                  <MarksTableHead>Title</MarksTableHead>
                  <MarksTableHead>Date</MarksTableHead>
                  <MarksTableHead align="right">Price / Share</MarksTableHead>
                  <MarksTableHead align="right">Fair Value</MarksTableHead>
                  <MarksTableHead align="right">Shares Held</MarksTableHead>
                  <MarksTableHead align="center">Filing</MarksTableHead>
                </MarksTableRow>
              </thead>
              <tbody>
                {[...filteredMarks].reverse().map((m: MutualFundMark, i: number) => (
                  <MarksTableRow key={i}>
                    <MarksTableCell>{getFundLabel(m)}</MarksTableCell>
                    <MarksTableCell>{m.title ?? '—'}</MarksTableCell>
                    <MarksTableCell>{formatIsoDate(m.reporting_date)}</MarksTableCell>
                    <MarksTableCell align="right">{formatCurrency(getPricePerShare(m))}</MarksTableCell>
                    <MarksTableCell align="right">{formatCurrency(toNum(m.fair_value_usd))}</MarksTableCell>
                    <MarksTableCell align="right">{m.shares_held?.toLocaleString() ?? '—'}</MarksTableCell>
                    <MarksTableCell align="center">
                      <IconButton
                        size="small"
                        component="a"
                        href={m.filing_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'primary.main' }}
                      >
                        <LaunchIcon sx={{ fontSize: '0.9rem' }} />
                      </IconButton>
                    </MarksTableCell>
                  </MarksTableRow>
                ))}
              </tbody>
            </MarksTable>
          )}
        </>
      )}
    </>
  );
};

export { EdgarMarksBody };
